import { useEffect, useState } from "react";
import { FaArrowLeft, FaCheck } from "react-icons/fa";
import { HiMail, HiPhone } from "react-icons/hi";
import { Link, useParams } from "react-router-dom";
import Badge from "../../components/Badge";
import Button from "../../components/Button";
import Card from "../../components/Card";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader from "../../components/PageHeader";
import StarRating from "../../components/StarRating";
import { api } from "../../services/api";

const SectionTitle = ({ children }) => (
  <h2 className="text-xl font-semibold mb-3 text-text">{children}</h2>
);

const InfoSection = ({ title, children, className = "" }) => (
  <div className={className}>
    <SectionTitle>{title}</SectionTitle>
    {children}
  </div>
);

const DAYS_OF_WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export default function TutorDetail() {
  const { id } = useParams();
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTutor();
  }, [id]);

  const loadTutor = async () => {
    try {
      const response = await api.get(`/tutors/${id}`);
      setTutor(response.tutor);
    } catch (error) {
      console.error("Error loading tutor:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  if (!tutor) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="text-center py-12">
          <p className="text-text/70 text-lg">Tutor not found</p>
          <Link to="/tutors" className="mt-4 inline-block">
            <Button variant="outline">Back to Tutors</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader title={`${tutor.name?.first} ${tutor.name?.last}`} />

      <Card>
        <div className="mb-6">
          <StarRating value={tutor.profile?.rating || 0} readonly size="md" />
        </div>

        <div className="space-y-6">
          <InfoSection title="About">
            <p className="text-text/80 leading-relaxed">
              {tutor.profile?.bio || (
                <span className="italic text-text/60">No bio available</span>
              )}
            </p>
          </InfoSection>

          <InfoSection title="Experience">
            <p className="text-text/80">
              Total Sessions Completed:{" "}
              <span className="font-semibold text-primary">
                {tutor.profile?.sessionsCompleted || 0}+
              </span>
            </p>
          </InfoSection>

          {tutor.profile?.subjects && tutor.profile.subjects.length > 0 && (
            <InfoSection title="Subjects">
              <div className="flex flex-wrap gap-2">
                {tutor.profile.subjects.map((subj, idx) => (
                  <Badge
                    key={idx}
                    label={`${subj.subjectId?.name || "Unknown"} · ${subj.expertiseLevel}`}
                    variant="primary"
                    size="medium"
                    className="capitalize font-medium"
                  />
                ))}
              </div>
            </InfoSection>
          )}

          {tutor.profile?.availability &&
            tutor.profile.availability.length > 0 && (
              <InfoSection title="Availability">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {tutor.profile.availability.map((slot, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-surface/50 rounded-lg border border-border"
                    >
                      <span className="font-medium text-text">
                        {DAYS_OF_WEEK[slot.dayOfWeek]}
                      </span>
                      <span className="text-sm text-text/70">
                        {slot.startTime} - {slot.endTime}
                      </span>
                    </div>
                  ))}
                </div>
              </InfoSection>
            )}

          {tutor.profile?.certificates &&
            tutor.profile.certificates.length > 0 && (
              <InfoSection title="Certificates">
                <ul className="space-y-2">
                  {tutor.profile.certificates.map((cert, idx) => (
                    <li key={idx} className="flex items-start text-text/80">
                      <FaCheck className="text-secondary font-bold mr-2 mt-0.5 flex-shrink-0" />
                      <span>{cert}</span>
                    </li>
                  ))}
                </ul>
              </InfoSection>
            )}

          <InfoSection title="Contact">
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-text/80">
                <HiMail className="w-5 h-5 text-primary" />
                <a
                  href={`mailto:${tutor.email}`}
                  className="hover:text-primary transition-colors"
                >
                  {tutor.email}
                </a>
              </div>
              {tutor.phone && (
                <div className="flex items-center gap-3 text-text/80">
                  <HiPhone className="w-5 h-5 text-primary" />
                  <a
                    href={`tel:${tutor.phone}`}
                    className="hover:text-primary transition-colors"
                  >
                    {tutor.phone}
                  </a>
                </div>
              )}
            </div>
          </InfoSection>
        </div>

        <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row gap-4">
          <Link
            to={`/student/bookings/new?tutorId=${tutor._id}`}
            className="flex-1"
          >
            <Button className="w-full">Book This Tutor</Button>
          </Link>
          <Link to="/tutors" className="flex-1">
            <Button
              variant="outline"
              className="w-full flex items-center justify-center gap-2"
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to Tutors
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
